export type GeoPosition = { longitude: number; latitude: number };

export type HemisphereResult = {
  ns: 'N' | 'S';
  ew: 'E' | 'W';
  nsLabel: 'North' | 'South';
  ewLabel: 'East' | 'West';
  combined: 'NE' | 'NW' | 'SE' | 'SW';
  combinedLabel: 'North East' | 'North West' | 'South East' | 'South West';
  latitudeDms: string;
  longitudeDms: string;
};

/**
 * Decimal degrees → `07° 12' 34.35"`.
 * `degreeDigits` pads degrees (2 for latitude, 3 for longitude).
 */
export function formatDecimalDegreesAsDms(decimal: number, degreeDigits: number): string {
  const abs = Math.abs(decimal);
  let degrees = Math.floor(abs);
  const minutesFloat = (abs - degrees) * 60;
  let minutes = Math.floor(minutesFloat);
  let seconds = Math.round((minutesFloat - minutes) * 60 * 100) / 100;

  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }
  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }

  const deg = String(degrees).padStart(degreeDigits, '0');
  const min = String(minutes).padStart(2, '0');
  const sec = seconds.toFixed(2).padStart(5, '0');
  return `${deg}° ${min}' ${sec}"`;
}

/** Hemisphere + DMS from GPS signs: latitude < 0 → South, longitude > 0 → East. */
export function hemisphereFromCoordinates(geo: GeoPosition): HemisphereResult {
  const ns: HemisphereResult['ns'] = geo.latitude < 0 ? 'S' : 'N';
  const ew: HemisphereResult['ew'] = geo.longitude < 0 ? 'W' : 'E';
  const nsLabel: HemisphereResult['nsLabel'] = ns === 'S' ? 'South' : 'North';
  const ewLabel: HemisphereResult['ewLabel'] = ew === 'W' ? 'West' : 'East';
  const combined = `${ns === 'S' ? 'S' : 'N'}${ew === 'W' ? 'W' : 'E'}` as HemisphereResult['combined'];
  const combinedLabel = `${nsLabel} ${ewLabel}` as HemisphereResult['combinedLabel'];
  return {
    ns,
    ew,
    nsLabel,
    ewLabel,
    combined,
    combinedLabel,
    latitudeDms: `${formatDecimalDegreesAsDms(geo.latitude, 2)} ${ns}`,
    longitudeDms: `${formatDecimalDegreesAsDms(geo.longitude, 3)} ${ew}`,
  };
}

type GeoOptions = {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
};

function getPosition(options: GeoOptions): Promise<GeoPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation tidak didukung di perangkat ini.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const longitude = pos.coords.longitude;
        const latitude = pos.coords.latitude;
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          reject(new Error('Koordinat GPS tidak valid.'));
          return;
        }
        resolve({ longitude, latitude });
      },
      (err) => reject(err),
      options,
    );
  });
}

function geolocationErrorMessage(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Izin lokasi ditolak. Aktifkan akses lokasi untuk situs ini di pengaturan browser/perangkat.';
    case err.POSITION_UNAVAILABLE:
      return 'Lokasi tidak dapat ditentukan (kCLErrorLocationUnknown). Coba di luar ruangan, matikan VPN, atau isi longitude/latitude manual.';
    case err.TIMEOUT:
      return 'Waktu habis saat mengambil GPS. Coba lagi atau isi koordinat manual.';
    default:
      return err.message || 'Gagal mengambil koordinat GPS.';
  }
}

/** Ambil GPS: akurasi tinggi dulu, lalu fallback akurasi rendah (lebih stabil di desktop/indoor). */
export async function readCurrentPosition(): Promise<GeoPosition> {
  try {
    return await getPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  } catch (highAccuracyErr) {
    if (
      highAccuracyErr instanceof GeolocationPositionError &&
      highAccuracyErr.code === highAccuracyErr.PERMISSION_DENIED
    ) {
      throw new Error(geolocationErrorMessage(highAccuracyErr));
    }
    try {
      return await getPosition({
        enableHighAccuracy: false,
        timeout: 25000,
        maximumAge: 120000,
      });
    } catch (second) {
      if (second instanceof GeolocationPositionError) {
        throw new Error(geolocationErrorMessage(second));
      }
      const msg = second instanceof Error ? second.message : 'Gagal mengambil koordinat GPS.';
      throw new Error(msg);
    }
  }
}

export function parseManualCoordinates(
  longitudeText: string,
  latitudeText: string,
): GeoPosition | null {
  const longitude = Number(longitudeText.trim().replace(',', '.'));
  const latitude = Number(latitudeText.trim().replace(',', '.'));
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return null;
  return { longitude, latitude };
}

/** Koordinat opsional dari input form (tidak memanggil GPS). */
export function resolveSurveyStartCoordinates(
  longitudeText: string,
  latitudeText: string,
): GeoPosition | null {
  return parseManualCoordinates(longitudeText, latitudeText);
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Gagal membaca file.'));
    reader.readAsDataURL(file);
  });
}
